import React, { useEffect } from 'react'
import Image from 'next/image'
import { Container, Typography } from '@cleartrip/ct-design-components'
import { getPopularDestinationUrl } from '@/utils/general/url'
import { IPopularDestinations } from '@/types/home'
import { useAppDispatch } from '@/redux/store/hooks'
import { setLineageId } from '@/redux/slices/lineageId'
import { getTestIdProps } from '@/utils/general/components'
import { PAGE_NAME } from '@/constants/pages/common'
import { HOME_PAGE_COMPONENTS_NAME } from '@/constants/components'
import { dataLayerGA } from '@/analytics/GA/dataLayerHelper'
import { ACTION_NAMES } from '@/constants/analytics'
import { ravenSDKHomeTrigger } from '@/analytics/raven/ravenSDKHelper'
import useInView from '@/utils/hooks/useInView'

interface TopDestinationProps {
    flightsData?: IPopularDestinations
    isHomePage?: boolean
}

function TopDestinations({ flightsData, isHomePage = false }: TopDestinationProps) {
    const dispatch = useAppDispatch()
    const { destinations = [] } = flightsData || {}

    useEffect(() => {
        if (destinations?.length > 0 && isHomePage) {
            const slicedDestinations = destinations.slice(0, 5)
            slicedDestinations.forEach((destination) => {
                ravenSDKHomeTrigger('a_merch_widget_impression', {
                    action_name: 'merch_impression',
                    object_id: 'populardestn_banner',
                    url: destination?.page_url ? `www.cleartrip.com${getPopularDestinationUrl(destination?.page_url)}` : 'n/a',
                    src: destination?.image_url || 'n/a'
                })
            })
        }
    }, [])

    const dalayerGA = (name: string) => {
        dataLayerGA({ clicked_option_name: name, section: 'General' }, ACTION_NAMES.popularDestinationsClick)
    }
    const onPopularDestinationClick = () => {
        dispatch(setLineageId())
    }
    const { ref } = useInView({
        triggerOnce: true,
        skip: !isHomePage,
        onChange: (inView) => {
            if (inView && isHomePage) {
                const slicedDestinations = destinations.slice(0, 5)
                slicedDestinations.forEach((destination, index) => {
                ravenSDKHomeTrigger('a_merch_widget_view', {
                    action_name: 'merch_view',
                    object_id: 'populardestn_banner',
                    url: destination?.page_url ? `www.cleartrip.com${getPopularDestinationUrl(destination?.page_url)}` : 'n/a',
                    src: destination?.image_url || 'n/a'
                })
            })
            }
        }
    })
    const handleDestinationClick = (destination: any, index: number) => {
        if (isHomePage) {
            ravenSDKHomeTrigger('a_merch_widget_click', {
                action_name: 'merch_click',
                object_id: 'populardestn_banner',
                url: destination?.page_url ? `www.cleartrip.com${getPopularDestinationUrl(destination?.page_url)}` : 'n/a',
                src: destination?.image_url || 'n/a'
            })
        }
    }

    const flightsListsBlock = destinations.slice(0, 5).map((destination, idx) => {
        //avoiding mutating props, using .slice()
        return (
            //to copy first five elements into a new array
            <a
                key={`popular-destination-${idx}`}
                href={getPopularDestinationUrl(destination.page_url) || ''}
                onClick={() => {
                    handleDestinationClick(destination, idx)
                    dalayerGA(destination.name)
                }}
                className={`td-none ${idx != destinations.length - 1 ? 'mr-4' : ''}`}
                target="_blank"
                {...getTestIdProps({
                    pageName: PAGE_NAME.HOME,
                    componentName: HOME_PAGE_COMPONENTS_NAME.TOP_DESTINATION,
                    testId: `${idx}`
                })}
            >
                <Container ref={ref}
                    height={'235px'}
                    width={'184px'}
                    className="o-hidden br-8"
                    onClick={onPopularDestinationClick}
                >
                    <Image height={235} width={184} src={destination.image_url} alt={destination.name} />
                    <Container className="p-absolute top-destination-overlay flex flex-column flex-end pl-16">
                        <Typography variant="HM2" className="px-4" color="neutral">
                            {destination.name}
                        </Typography>
                        <Typography variant="B2" className="px-1 px-4 pb-4 pb-0" color="neutral">
                            {destination.property_count} Properties
                        </Typography>
                    </Container>
                </Container>
            </a>
        )
    })

    return <Container className="flex p-relative">{flightsListsBlock}</Container>
}

export default TopDestinations
